import { useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export function useTabUrlState<T extends string>(
  tabs: T[],
  defaultValue = tabs[0],
) {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  const tabIndex = tabs.indexOf(location.hash.slice(1) as T);
  const tab = tabs[tabIndex] ?? defaultValue;

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const hasNoHash = location.hash.slice(1) === "";
  useEffect(() => {
    /* If there's no hash, set the hash to the first tab. We need to replace to avoid creating an entry in the browser
     * history, which would required the back button to be pressed twice to go back to the previous page. */
    if (hasNoHash) {
      navigate({ ...locationRef.current, hash: tab }, { replace: true });
    }
  }, [hasNoHash, navigate, tab]);

  const setTab = useCallback(
    (tab: string) => navigate({ ...locationRef.current, hash: tab }),
    [navigate],
  );

  return [tab, setTab] as const;
}
